import { useEffect, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Save, Send } from 'lucide-react'
import { addStaff, listStaff, staffLinkAgain, updateStaff } from '../../api/practice'
import ErrorBanner from '../../components/ErrorBanner'
import Spinner from '../../components/Spinner'
import { BackLink, DocHeader } from '../../components/PageParts'
import { useToast } from '../../components/Toast.jsx'
import { Field } from '../master/masterShared'
import { CopyLink } from './practiceShared'
import { staffStatus } from './Staff'

const KINDS = [
  { admin: true, label: 'Admin', desc: 'Opens every client, adds clients and staff, and changes the practice’s settings.' },
  { admin: false, label: 'Staff', desc: 'Opens only the clients you choose below.' },
]

const linkText = (r, email) => (r.email_sent
  ? `We emailed ${email} a link to choose their password. You can also pass it on yourself; it works for 7 days.`
  : `We couldn’t email ${email}, so pass this link on yourself; it works for 7 days.`)

/** Someone in the practice: their details, admin or staff, and the clients
    they open. A fresh form for each person (and for a new one), so the link
    made when adding someone shows on their page. */
export default function StaffPage() {
  const { userId } = useParams()
  return <StaffForm key={userId || 'new'} userId={userId} />
}

function StaffForm({ userId }) {
  const isNew = !userId
  const nav = useNavigate()
  const { state } = useLocation()
  const qc = useQueryClient()
  const { notify } = useToast()
  const { data, isLoading } = useQuery({ queryKey: ['practice-staff'], queryFn: listStaff })
  const person = !isNew && data ? data.staff.find((p) => String(p.user_id) === String(userId)) : null
  const isMe = !!person && person.user_id === data?.me
  const [form, setForm] = useState({ full_name: '', email: '', username: '', admin: false, active: true, clients: [] })
  const [fields, setFields] = useState({})
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  const [sent, setSent] = useState(state?.created || null)
  useEffect(() => {
    if (person) setForm({ full_name: person.full_name, email: person.email || '', username: person.username, admin: person.admin, active: person.active, clients: person.clients })
  }, [person?.user_id]) // eslint-disable-line react-hooks/exhaustive-deps

  const set = (patch) => {
    setForm((f) => ({ ...f, ...patch }))
    setFields((e) => (Object.keys(patch).some((k) => e[k]) ? { ...e, ...Object.fromEntries(Object.keys(patch).map((k) => [k, undefined])) } : e))
  }
  const toggle = (id, on) => set({ clients: on ? [...form.clients, id] : form.clients.filter((c) => c !== id) })
  const clients = data?.clients || []

  const save = async (e) => {
    e && e.preventDefault()
    setSaving(true); setError(''); setFields({})
    try {
      if (isNew) {
        const r = await addStaff({ ...form, email: form.email.trim().toLowerCase() })
        await qc.invalidateQueries({ queryKey: ['practice-staff'] })
        notify(`${form.full_name} added`)
        nav(`/practice/staff/${r.user_id}`, { replace: true, state: { created: { ...r, email: form.email.trim().toLowerCase() } } })
      } else {
        await updateStaff(person.user_id, { full_name: form.full_name, email: form.email.trim().toLowerCase(), admin: form.admin, active: form.active, clients: form.clients })
        await qc.invalidateQueries({ queryKey: ['practice-staff'] })
        notify('Saved')
        nav('/practice/staff')
      }
    } catch (err) {
      setFields(err.fields || {})
      setError(Object.keys(err.fields || {}).length ? '' : (err.message || 'Couldn’t save.'))
    }
    setSaving(false)
  }
  const again = async () => {
    setError('')
    try {
      const r = await staffLinkAgain(person.user_id)
      setSent({ ...r, email: person.email })
      notify(r.email_sent ? 'New link emailed' : 'New link ready to pass on')
    } catch (err) { setError(err.message || 'Couldn’t make a new link.') }
  }

  if (isLoading) return <div className="ci-form-page"><Spinner label="Loading…" /></div>
  if (!isNew && !person) return <div className="ci-form-page"><ErrorBanner message="That person isn’t in your practice." /></div>
  return (
    <form className="ci-form-page" onSubmit={save} noValidate>
      <DocHeader back={<BackLink to="/practice/staff" label="Staff" />}
        title={isNew ? 'Add someone' : person.full_name}
        badge={!isNew && staffStatus(person)}
        meta={isNew ? 'Someone in your practice who works in your clients’ books.' : `@${person.username}${isMe ? ' · you' : ''}`} />
      {error && <ErrorBanner message={error} />}
      {sent?.link && (
        <div className="ci-form-meta-card mf-form">
          <div className="mf-card-title">Their link to choose a password</div>
          <CopyLink link={sent.link} text={linkText(sent, sent.email || person?.email)} label="Password link" />
        </div>
      )}

      <div className="ci-form-meta-card mf-form">
        <div className="mf-card-title">Their details</div>
        <div className="ci-form-meta-top three">
          <Field label="Full name" error={fields.full_name}>
            <input value={form.full_name} onChange={(e) => set({ full_name: e.target.value })} autoFocus={isNew} />
          </Field>
          <Field label="Email" error={fields.email} hint={isNew ? 'We email them a link to choose their password.' : undefined}>
            <input type="email" value={form.email} onChange={(e) => set({ email: e.target.value })} placeholder="name@firm.com" autoComplete="off" />
          </Field>
          {isNew && (
            <Field label="Username" error={fields.username} hint="They sign in with your practice’s Company ID and this.">
              <input value={form.username} onChange={(e) => set({ username: e.target.value })} autoComplete="off" />
            </Field>
          )}
        </div>
        {!isNew && (
          <div className="mf-checks">
            <label className="ci-form-toggle mf-check">
              <input type="checkbox" checked={form.active} disabled={isMe} onChange={(e) => set({ active: e.target.checked })} />
              Can sign in
            </label>
            {isMe && <span className="mf-hint">You can’t switch yourself off.</span>}
          </div>
        )}
        {!isNew && person.waiting && person.active && !sent && (
          <div className="pr-owner">
            <span className="cell-sub">They haven’t chosen a password yet.</span>
            <button type="button" className="ci-btn ci-btn-ghost ci-btn-sm" onClick={again}><Send size={14} />Send a new link</button>
          </div>
        )}
      </div>

      <div className="ci-form-meta-card mf-form">
        <div className="mf-card-title">What they can do</div>
        <div className="mf-role-grid">
          {KINDS.map((k) => {
            const locked = isMe && !k.admin
            return (
              <label key={k.label} className={`mf-role-option${form.admin === k.admin ? ' on' : ''}`}>
                <input type="radio" name="kind" checked={form.admin === k.admin} disabled={locked} onChange={() => set({ admin: k.admin })} />
                <span><strong>{k.label}</strong><span>{locked ? 'You can’t take away your own admin.' : k.desc}</span></span>
              </label>
            )
          })}
        </div>
      </div>

      {!form.admin && (
        <div className="ci-form-meta-card mf-form">
          <div className="mf-card-title">Clients they can open</div>
          {clients.length === 0 ? <p className="doc-hint pr-card-hint">Your practice has no clients yet.</p> : (
            <div className="mf-role-grid">
              {clients.map((c) => (
                <label key={c.client_id} className={`mf-role-option${form.clients.includes(c.client_id) ? ' on' : ''}`}>
                  <input type="checkbox" checked={form.clients.includes(c.client_id)} onChange={(e) => toggle(c.client_id, e.target.checked)} />
                  <span><strong>{c.name}</strong><span>{c.plan}</span></span>
                </label>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="ci-form-action-bar">
        <div className="ci-form-action-left">
          <button type="submit" className="ci-btn ci-btn-primary" disabled={saving}><Save size={14} />{saving ? 'Saving…' : (isNew ? 'Add them' : 'Save')}</button>
          <button type="button" className="ci-btn ci-btn-ghost" onClick={() => nav('/practice/staff')}>{sent ? 'Done' : 'Cancel'}</button>
        </div>
      </div>
    </form>
  )
}
